import React, { useCallback, useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";
import { useTranslation } from "../../i18n";

/**
 * Shared chrome for every rail widget: header, collapse toggle, scroll body.
 *
 * All three widgets use this so the rail reads as one family, and so "collapse
 * this section" behaves identically everywhere rather than being reinvented per
 * widget.
 *
 * Collapsed state is per-user and per-widget, kept in localStorage. It is a view
 * preference rather than a setting, so it deliberately never reaches the server
 * — org admins own which widgets exist and in what order; the reader owns
 * whether one is currently folded away.
 */

const storageKey = (widgetKey) => `widget.collapsed.${widgetKey}`;

function readCollapsed(widgetKey, fallback) {
  try {
    const raw = window.localStorage.getItem(storageKey(widgetKey));
    if (raw === null) return fallback;
    return raw === "1";
  } catch {
    return fallback;
  }
}

export default function WidgetShell({
  widgetKey,
  icon: Icon,
  title,
  accentClass = "text-purple-600",
  headerExtra,
  // Below the rail cutover widgets stack above the feed, so they start folded:
  // three expanded widgets would bury the feed on a phone.
  defaultCollapsed = false,
  scroll = true,
  compact = false,
  // Height budget so all three widgets fit one screen without the page
  // scrolling: ~48px header each, plus these bodies, plus gaps.
  maxBody = 200,
  children,
}) {
  const { t } = useTranslation("widgets");
  const [collapsed, setCollapsed] = useState(() =>
    readCollapsed(widgetKey, defaultCollapsed)
  );

  // A stored choice wins; otherwise crossing the cutover re-applies the default
  // for that layout, so a phone doesn't inherit a desktop's expanded rail.
  useEffect(() => {
    setCollapsed(readCollapsed(widgetKey, defaultCollapsed));
  }, [widgetKey, defaultCollapsed]);

  // The persist deliberately happens outside the state updater: React invokes
  // updaters twice in StrictMode to surface impurity, so a write in there runs
  // twice and the stored value can disagree with what was rendered.
  const toggle = useCallback(() => {
    const next = !collapsed;
    setCollapsed(next);
    try {
      window.localStorage.setItem(storageKey(widgetKey), next ? "1" : "0");
    } catch {
      /* private browsing — the choice just won't persist */
    }
  }, [collapsed, widgetKey]);



  return (
    <section className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100">
        {Icon && <Icon className={`w-4 h-4 flex-shrink-0 ${accentClass}`} />}
        <button
          type="button"
          onClick={toggle}
          aria-expanded={!collapsed}
          title={typeof title === "string" ? title : undefined}
          className="flex-1 min-w-0 truncate text-left text-sm font-semibold text-gray-900 hover:text-purple-700 transition-colors"
        >
          {title}
        </button>
        {headerExtra}
        <button
          type="button"
          onClick={toggle}
          aria-expanded={!collapsed}
          aria-label={collapsed ? t("shell.expand", { title }) : t("shell.collapse", { title })}
          className="text-gray-400 hover:text-gray-700 transition-colors flex-shrink-0"
        >
          <ChevronDown
            className={`w-4 h-4 transition-transform duration-200 ${
              collapsed ? "-rotate-90" : ""
            }`}
          />
        </button>
      </div>

      {!collapsed && (
        <div
          className={scroll ? "overflow-y-auto" : undefined}
          style={scroll ? { maxHeight: `${maxBody}px` } : undefined}
        >
          {children}
        </div>
      )}
    </section>
  );
}
